import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const modifierStateMap: Record<string, 'Shift' | 'Control' | 'Alt' | 'Meta'> = {
  shift: 'Shift',
  control: 'Control',
  ctrl: 'Control',
  alt: 'Alt',
  option: 'Alt',
  meta: 'Meta',
  cmd: 'Meta',
  command: 'Meta',
}

const normalizeKey = (key: string) =>
  modifierStateMap[key.toLowerCase()]?.toLowerCase() ?? key.toLowerCase()

const getNormalizedKeys = (keys: string | readonly string[]) => {
  const normalizedKeys = (Array.isArray(keys) ? keys : [keys]).map(normalizeKey)
  return Array.from(new Set(normalizedKeys))
}

const useIsKeyPressed = (keys: string | readonly string[]) => {
  const normalizedKeySignature = useMemo(() => getNormalizedKeys(keys).sort().join('|'), [keys])
  const normalizedKeys = useMemo(
    () => (normalizedKeySignature ? normalizedKeySignature.split('|') : []),
    [normalizedKeySignature],
  )
  const pressedKeysRef = useRef(new Set<string>())
  const [isPressed, setIsPressed] = useState(false)

  const updatePressedState = useCallback(() => {
    const nextIsPressed = normalizedKeys.some(key => pressedKeysRef.current.has(key))
    setIsPressed(previousIsPressed =>
      previousIsPressed === nextIsPressed ? previousIsPressed : nextIsPressed,
    )
  }, [normalizedKeys])

  const resetPressedState = useCallback(() => {
    pressedKeysRef.current.clear()
    setIsPressed(false)
    return false
  }, [])

  const syncPressedState = useCallback(
    (event: KeyboardEvent | MouseEvent) => {
      const nextIsPressed = normalizedKeys.some(key => {
        const modifierState = modifierStateMap[key]
        if (modifierState) return event.getModifierState(modifierState)

        if ('key' in event) {
          return normalizeKey(event.key) === key && event.type !== 'keyup'
        }

        return false
      })

      setIsPressed(previousIsPressed =>
        previousIsPressed === nextIsPressed ? previousIsPressed : nextIsPressed,
      )

      return nextIsPressed
    },
    [normalizedKeys],
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      pressedKeysRef.current.add(normalizeKey(event.key))
      updatePressedState()
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      pressedKeysRef.current.delete(normalizeKey(event.key))
      updatePressedState()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) resetPressedState()
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', resetPressedState)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', resetPressedState)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [resetPressedState, updatePressedState])

  return {
    isPressed,
    resetPressedState,
    syncPressedState,
  }
}

export default useIsKeyPressed
